/**
 * Timezone arithmetic, with the zone as a PARAMETER (#2167, ADR-0035 D1; the
 * module's home is the `lib/merchant-tz.ts` row of D4 in
 * `docs/adr/0035-resource-kit-independence.md`).
 *
 * The host's `lib/merchant-tz.ts` owns the merchant zone as STATE — a cached
 * value bootstrapped from the backend and mirrored in `localStorage`. That
 * ownership stays there. What moves here is the part that has no state at all:
 * given a zone name and a reading, what instant is it. `serialize.ts` needs
 * exactly that and nothing else, which is what lets the whole query layer
 * compile with no host module behind it.
 *
 * Nothing in this file is user-facing, so the fixed `en-CA` locale below is a
 * format choice rather than locale policy: it pins ASCII digits and a
 * parseable field order. The repo's ban on a hardcoded locale is about text an
 * operator READS.
 */

/**
 * How far `tz` is from UTC at a given INSTANT, in milliseconds (negative west
 * of Greenwich). Reads the zone's own rendering of the instant and diffs it
 * against the instant itself, so DST is handled by the platform's tz database
 * rather than by a table we would have to maintain.
 *
 * `en-CA` + `hourCycle: 'h23'` are format choices, not locale policy: they pin
 * ASCII digits and a 0-23 hour so the parts parse. (The i18n rule bans
 * hardcoding a locale for USER-FACING formatting; nothing here is displayed.)
 */
export function tzOffsetMsAt(tz: string, at: Date): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);
  const p: Record<string, string> = {};
  for (const part of parts) p[part.type] = part.value;
  const asIfUtc = Date.UTC(
    Number(p['year']),
    Number(p['month']) - 1,
    Number(p['day']),
    Number(p['hour']),
    Number(p['minute']),
    Number(p['second']),
  );
  // Diff against `at` FLOORED TO THE SECOND, because `formatToParts` renders
  // whole seconds and `Date.UTC` therefore reconstructs one. Diffing against
  // the raw instant would fold the input's own milliseconds into the "offset"
  // — a real zone offset is always a whole number of minutes — and the caller
  // would then subtract them again. Invisible while every caller passed a
  // zeroed instant; a `…:59.999` filter bound is what exposed it, landing a
  // full second past the end of the day it named.
  return asIfUtc - (at.getTime() - at.getUTCMilliseconds());
}

/**
 * The UTC instant a WALL CLOCK reading in `timeZone` names, as an ISO-8601
 * string. `wall` is a zone-less `YYYY-MM-DDTHH:MM:SS.sss` — what a clock on
 * that wall would read — and the answer is the instant at which it reads it.
 *
 * Two passes, and the second one is load-bearing. The first treats the wall
 * clock as if it were UTC and corrects by the offset AT THAT GUESS; on a DST
 * transition day the guess can land on the far side of the jump, so the offset
 * is re-read at the corrected instant and applied again. A single pass gets
 * one edge of 2026-03-08 (US spring-forward) an hour wrong.
 *
 * Two classes of wall-clock reading are not instants at all: the hour skipped
 * by spring-forward (which never happens) and the hour repeated by fall-back
 * (which happens twice). Both ARE reachable — a filter bound completing a
 * `THH:mm` / `THH:mm:ss` value from a pre-#2111 URL can name any hour, so
 * `2026-03-08T02:30` and `2026-11-01T01:30` both arrive here. They are answered
 * deterministically, and measured to agree with `date-fns-tz`'s `fromZonedTime`
 * for `America/Los_Angeles`; the skipped hour resolves forward into the day it
 * names, never outside the window. The day edges #2105 introduced are not
 * themselves ambiguous in the zone fastcade actually serves —
 * `America/Los_Angeles` transitions at 02:00 local — but that is a fact about
 * that zone, not a property of this function: zones exist that transition at
 * midnight, and there the `00:00:00.000` bound would be one of these readings
 * too.
 */
export function wallTimeToUtcIso(wall: string, timeZone: string): string {
  const guess = Date.parse(`${wall}Z`);
  const firstPass = guess - tzOffsetMsAt(timeZone, new Date(guess));
  const settled = guess - tzOffsetMsAt(timeZone, new Date(firstPass));
  return new Date(settled).toISOString();
}

/** One formatter per zone: `useToday()` reads the day on every store wake. */
const calendarDayFormats = new Map<string, Intl.DateTimeFormat>();

/**
 * The calendar day (`YYYY-MM-DD`) `at` falls on in `timeZone` (#2780). The
 * kit's `useToday()` reads "today" through this, with the zone from the host's
 * `format.timeZone` seam.
 *
 * `en-CA` is a format choice, not locale policy: it renders the date as
 * `YYYY-MM-DD` with ASCII digits. Nothing here is displayed.
 */
export function calendarDayIn(timeZone: string, at: Date): string {
  let format = calendarDayFormats.get(timeZone);
  if (format === undefined) {
    format = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    calendarDayFormats.set(timeZone, format);
  }
  return format.format(at);
}
